import {
  UseCeloConfig,
  ApiResponse,
  ApiError,
  AuthError,
  NetworkError
} from '../types';

// Simple config interface for payment client - expects resolved values
interface PaymentClientConfig {
  appId: string;
  publicKey: string;
  apiHost?: string;
  userId?: string; // Simple string, not function
}

import {
  CheckoutOptions,
  CheckoutSession,
  PaymentMethodOptions,
  PortalSessionOptions,
  GatewayInfo,
  SubscriptionUpdateOptions,
  SubscriptionCancelOptions,
  SubscriptionPauseOptions,
  SubscriptionDetails,
  PaymentMethod,
  Invoice,
  RefundRequest,
  RefundResponse,
  CheckoutResponse,
  PortalResponse,
  SubscriptionResponse,
  CurrentSubscriptionResponse,
  CurrentSubscriptionData,
  CancelResponse,
  PauseResponse,
  PaymentMethodsResponse,
  InvoicesResponse,
  PaymentError
} from '../types/payment';

/**
 * Payment Gateway Client for multi-gateway support
 * Works with both OAuth and Adapter integrations
 * Follows the same patterns as UseCeloApiClient
 */
export class PaymentGatewayClient {
  private config: PaymentClientConfig;
  private authToken: string | null = null;
  private gatewayInfoCache: { info: GatewayInfo; timestamp: number } | null = null;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  constructor(config: PaymentClientConfig) {
    this.config = {
      apiHost: 'https://usecelo.com',
      ...config
    };
  }

  /**
   * Set authentication token (consistent with api-client)
   */
  setAuthToken(token: string): void {
    this.authToken = token;
  }

  /**
   * Clear cached gateway info
   */
  clearCache(): void {
    this.gatewayInfoCache = null;
  }

  /**
   * Generic request method (follows api-client pattern)
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.config.apiHost}${endpoint}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'x-api-key': this.config.publicKey,
      ...((options.headers as Record<string, string>) || {})
    };

    // Add auth token if available
    if (this.authToken) {
      headers.Authorization = `Bearer ${this.authToken}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers
      });

      if (!response.ok) {
        if (response.status === 401) {
          this.authToken = null; // Clear invalid token
          throw new AuthError('Invalid API key or authentication token');
        }

        const errorData = await response.json().catch(() => ({}) as PaymentError);
        throw new ApiError(
          errorData.message || `HTTP ${response.status}`,
          response.status.toString(),
          response.status
        );
      }

      const data = await response.json() as ApiResponse<T>;

      if (!data.success) {
        throw new ApiError(data.error || 'API request failed', '400', 400);
      }

      return data.data!;
    } catch (error) {
      if (error instanceof ApiError || error instanceof AuthError) {
        throw error;
      }

      // Network or other errors
      throw new NetworkError(`Request failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get configured gateway information for the app (with caching)
   */
  async getGatewayInfo(): Promise<GatewayInfo> {
    // Check cache first
    if (this.gatewayInfoCache &&
        Date.now() - this.gatewayInfoCache.timestamp < this.CACHE_DURATION) {
      return this.gatewayInfoCache.info;
    }

    const info = await this.request<GatewayInfo>(
      `/api/public/gateway/${this.config.appId}/info`
    );

    // Cache the result
    this.gatewayInfoCache = {
      info,
      timestamp: Date.now()
    };

    return info;
  }

  /**
   * Create a checkout session for any configured gateway
   * This works with both OAuth and Adapter integrations
   */
  async createCheckout(options: CheckoutOptions): Promise<CheckoutSession> {
    const userId = options.userId || this.config.userId;
    if (!userId) {
      throw new AuthError('User ID is required for checkout');
    }

    // Validate required fields
    if (!options.planId) {
      throw new ApiError('Plan ID is required for checkout', '400', 400);
    }

    const response = await this.request<CheckoutSession>(
      '/api/public/checkout/session',
      {
        method: 'POST',
        body: JSON.stringify({
          appId: this.config.appId,
          userId,
          ...options
        })
      }
    );

    console.log('🔍 PaymentClient: Response from API:', response);

    // The request method already extracts the data, so response IS the data
    if (!response || !response.url) {
      console.log('❌ PaymentClient: No response or URL found');
      throw new ApiError('Failed to create checkout session', '400', 400);
    }

    return response;
  }

  /**
   * Create a customer portal session (if supported by gateway)
   */
  async createPortalSession(options: PortalSessionOptions = {}): Promise<{ url: string }> {
    const userId = options.userId || this.config.userId;
    if (!userId) {
      throw new AuthError('User ID is required for portal session');
    }

    // Check if portal is supported
    const gatewayInfo = await this.getGatewayInfo();
    if (!gatewayInfo.capabilities.portal) {
      throw new ApiError('Customer portal is not supported by the configured payment gateway', '400', 400);
    }

    const response = await this.request<PortalResponse>(
      '/api/public/portal/session',
      {
        method: 'POST',
        body: JSON.stringify({
          appId: this.config.appId,
          userId,
          ...options
        })
      }
    );

    if (!response.data) {
      throw new ApiError(response.error || 'Failed to create portal session', '400', 400);
    }

    return response.data;
  }

  /**
   * Get payment methods for a user
   */
  async getPaymentMethods(userId?: string): Promise<PaymentMethod[]> {
    const userParam = userId || this.config.userId;
    if (!userParam) {
      throw new AuthError('User ID is required to get payment methods');
    }

    const response = await this.request<PaymentMethodsResponse>(
      `/api/public/payment-methods?userId=${encodeURIComponent(userParam)}&appId=${encodeURIComponent(this.config.appId)}`
    );

    return response.data || [];
  }

  /**
   * Update default payment method
   */
  async updateDefaultPaymentMethod(paymentMethodId: string, userId?: string): Promise<PaymentMethod> {
    const userParam = userId || this.config.userId;
    if (!userParam) {
      throw new AuthError('User ID is required for payment method update');
    }

    return this.request<PaymentMethod>(
      '/api/public/payment-methods/default',
      {
        method: 'POST',
        body: JSON.stringify({
          appId: this.config.appId,
          userId: userParam,
          paymentMethodId
        })
      }
    );
  }

  /**
   * Add a new payment method (returns URL for payment method addition flow)
   */
  async addPaymentMethod(options: PaymentMethodOptions = {}): Promise<{ url: string }> {
    const userId = options.userId || this.config.userId;
    if (!userId) {
      throw new AuthError('User ID is required for adding payment method');
    }

    return this.request<{ url: string }>(
      '/api/public/payment-methods/add',
      {
        method: 'POST',
        body: JSON.stringify({
          appId: this.config.appId,
          userId,
          ...options
        })
      }
    );
  }

  /**
   * Remove a payment method
   */
  async removePaymentMethod(paymentMethodId: string, userId?: string): Promise<{ success: boolean }> {
    const userParam = userId || this.config.userId;
    if (!userParam) {
      throw new AuthError('User ID is required for removing payment method');
    }

    return this.request<{ success: boolean }>(
      `/api/public/payment-methods/${paymentMethodId}`,
      {
        method: 'DELETE',
        body: JSON.stringify({
          appId: this.config.appId,
          userId: userParam
        })
      }
    );
  }

  /**
   * Get current subscription details
   */
  async getSubscription(userId?: string): Promise<CurrentSubscriptionData | null> {
    const userParam = userId || this.config.userId;
    if (!userParam) {
      throw new AuthError('User ID is required to get subscription');
    }

    const response = await this.request<CurrentSubscriptionData>(
      `/api/public/subscriptions/current?externalId=${encodeURIComponent(userParam)}&appId=${encodeURIComponent(this.config.appId)}`
    );

    return response || null;
  }

  /**
   * Update subscription plan
   */
  async updateSubscription(options: SubscriptionUpdateOptions): Promise<SubscriptionDetails> {
    const userId = options.userId || this.config.userId;
    if (!userId) {
      throw new AuthError('User ID is required for subscription update');
    }

    const response = await this.request<SubscriptionResponse>(
      '/api/public/subscriptions/update',
      {
        method: 'POST',
        body: JSON.stringify({
          appId: this.config.appId,
          userId,
          ...options
        })
      }
    );

    if (!response.data) {
      throw new ApiError(response.error || 'Failed to update subscription', '400', 400);
    }

    return response.data;
  }

  /**
   * Cancel subscription
   */
  async cancelSubscription(options: SubscriptionCancelOptions = {}): Promise<CancelResponse> {
    const userId = options.userId || this.config.userId;
    if (!userId) {
      throw new AuthError('User ID is required for subscription cancellation');
    }

    return this.request<CancelResponse>(
      '/api/public/subscriptions/cancel',
      {
        method: 'POST',
        body: JSON.stringify({
          externalId: userId,
          immediate: options.immediate,
          reason: options.reason,
          feedback: options.feedback,
        })
      }
    );
  }

  /**
   * Pause subscription (if supported)
   */
  async pauseSubscription(options: SubscriptionPauseOptions = {}): Promise<PauseResponse> {
    const userId = options.userId || this.config.userId;
    if (!userId) {
      throw new AuthError('User ID is required for subscription pause');
    }

    // Check if pause is supported
    const gatewayInfo = await this.getGatewayInfo();
    if (!gatewayInfo.capabilities.subscriptionPause) {
      throw new ApiError('Subscription pause is not supported by the configured payment gateway', '400', 400);
    }

    return this.request<PauseResponse>(
      '/api/public/subscriptions/pause',
      {
        method: 'POST',
        body: JSON.stringify({
          appId: this.config.appId,
          userId,
          until: options.until?.toISOString(),
          behavior: options.behavior,
          resumeType: options.resumeType
        })
      }
    );
  }

  /**
   * Resume subscription
   */
  async resumeSubscription(userId?: string): Promise<SubscriptionDetails> {
    const userParam = userId || this.config.userId;
    if (!userParam) {
      throw new AuthError('User ID is required for subscription resume');
    }

    const response = await this.request<SubscriptionResponse>(
      '/api/public/subscriptions/resume',
      {
        method: 'POST',
        body: JSON.stringify({
          appId: this.config.appId,
          userId: userParam
        })
      }
    );

    if (!response.data) {
      throw new ApiError(response.error || 'Failed to resume subscription', '400', 400);
    }

    return response.data;
  }

  /**
   * Get invoices for a user
   */
  async getInvoices(userId?: string, limit = 10, cursor?: string): Promise<InvoicesResponse> {
    const userParam = userId || this.config.userId;
    if (!userParam) {
      throw new AuthError('User ID is required to get invoices');
    }

    const params = new URLSearchParams({
      userId: userParam,
      appId: this.config.appId,
      limit: limit.toString()
    });

    if (cursor) {
      params.append('cursor', cursor);
    }

    return this.request<InvoicesResponse>(
      `/api/public/invoices?${params.toString()}`
    );
  }

  /**
   * Download invoice
   */
  async downloadInvoice(invoiceId: string, userId?: string): Promise<{ url: string }> {
    const userParam = userId || this.config.userId;
    if (!userParam) {
      throw new AuthError('User ID is required to download invoice');
    }

    return this.request<{ url: string }>(
      `/api/public/invoices/${invoiceId}/download?userId=${encodeURIComponent(userParam)}&appId=${encodeURIComponent(this.config.appId)}`
    );
  }

  /**
   * Request a refund
   */
  async requestRefund(request: RefundRequest): Promise<RefundResponse> {
    return this.request<RefundResponse>(
      '/api/public/refunds',
      {
        method: 'POST',
        body: JSON.stringify({
          appId: this.config.appId,
          ...request
        })
      }
    );
  }

  /**
   * Check if the app has a payment gateway configured
   */
  async isPaymentConfigured(): Promise<boolean> {
    try {
      const info = await this.getGatewayInfo();
      return info.isConfigured;
    } catch {
      return false;
    }
  }

  /**
   * Get checkout URL directly (convenience method)
   */
  async getCheckoutUrl(planId: string, options: Partial<CheckoutOptions> = {}): Promise<string> {
    const session = await this.createCheckout({
      planId,
      ...options
    });
    return session.url;
  }

  /**
   * Get portal URL directly (convenience method)
   */
  async getPortalUrl(options?: PortalSessionOptions): Promise<string> {
    const session = await this.createPortalSession(options);
    return session.url;
  }

  /**
   * Check gateway capabilities
   */
  async checkCapability(capability: keyof GatewayInfo['capabilities']): Promise<boolean> {
    const info = await this.getGatewayInfo();
    return info.capabilities[capability] || false;
  }
}